
import React from 'react';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Payment, PaymentHistoryExportData } from '../types';
import { formatCurrency } from '@/utils/formatters';

interface ExportButtonProps {
  payments: Payment[];
  loading: boolean;
}

export const ExportButton = ({ payments, loading }: ExportButtonProps) => {
  const handleExportToExcel = () => {
    if (payments.length === 0) {
      toast.error("No payment data to export");
      return;
    }

    const exportData: PaymentHistoryExportData[] = payments.map(payment => ({
      date: format(new Date(payment.date), 'yyyy-MM-dd'),
      type: payment.type === 'salary' ? 'Teacher Salary' : 'Student Fee',
      recipient: payment.recipient,
      amount: formatCurrency(payment.amount).replace('$', ''),
      status: payment.status,
      reference: payment.reference,
      description: payment.description,
      paymentMethod: payment.paymentMethod || '-',
      refundAmount: payment.refundAmount ? formatCurrency(payment.refundAmount).replace('$', '') : '-',
      refundDate: payment.refundDate ? format(new Date(payment.refundDate), 'yyyy-MM-dd') : '-',
      planModifications: payment.planModifications?.length 
        ? payment.planModifications.map(mod => 
            `${format(new Date(mod.date), 'yyyy-MM-dd')}: ${formatCurrency(mod.previousAmount)} → ${formatCurrency(mod.newAmount)}`
          ).join('; ')
        : '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payment History");
    
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    saveAs(data, `payment-history-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast.success("Payment history exported successfully");
  };

  return (
    <Button 
      variant="outline" 
      size="sm" 
      onClick={handleExportToExcel} 
      disabled={payments.length === 0 || loading}
      className="gap-2"
    >
      <Download className="h-4 w-4" />
      Export to Excel
    </Button>
  );
};
